import { Timestamp } from 'firebase/firestore';

/**
 * Business type selection from onboarding
 */
export type BusinessType = 'dealer' | 'garage' | 'dealer_garage';

/**
 * First action preference from onboarding
 * @deprecated Use EnvironmentChoice instead. Will be removed in v2.0.
 */
export type FirstActionPreference = 'vehicle' | 'invoice' | 'planning';

/**
 * Environment choice from onboarding step 3
 * - 'empty': Start with a clean slate, no demo data
 * - 'demo': Start with pre-filled demo data to explore features
 */
export type EnvironmentChoice = 'empty' | 'demo';

/**
 * Fullscreen onboarding completion state
 * Stored directly on /teams/{teamId}
 */
export interface TeamOnboardingCompletion {
  /** When the fullscreen onboarding was completed */
  onboardingCompletedAt?: Timestamp | null;
  /** Version of onboarding flow completed (for future migrations) */
  onboardingVersion?: number;
  /** Whether onboarding was skipped (true) or completed normally (false/undefined) */
  onboardingSkipped?: boolean;
  /** Business type selected during onboarding */
  businessType?: BusinessType;
  /** First action preference selected during onboarding
   * @deprecated Use environmentChoice instead. Will be removed in v2.0.
   */
  firstActionPreference?: FirstActionPreference;
  /** Environment choice: 'empty' for clean slate, 'demo' for sample data */
  environmentChoice?: EnvironmentChoice;
}

/**
 * Onboarding checklist item types
 */
export type OnboardingChecklistItem =
  | 'vehicle'
  | 'customer'
  | 'salesInvoice'
  | 'purchaseInvoice'
  | 'task'
  | 'appointment';

/**
 * Team onboarding state
 * Stored at: /teams/{teamId}/settings/onboarding
 */
export interface TeamOnboarding {
  /** When the team was created */
  createdAt: Timestamp;

  /** When the team completed initial setup (all checklist items true) */
  initializedAt?: Timestamp | null;

  /** Checklist of first-time actions */
  checklist: {
    vehicle: boolean;
    customer: boolean;
    salesInvoice: boolean;
    purchaseInvoice: boolean;
    task: boolean;
    appointment: boolean;
  };
}

/**
 * User-specific onboarding preferences
 * Stored at: /users/{uid}/private/onboarding
 */
export interface UserOnboardingPrefs {
  /** When the user dismissed the first-run banner */
  bannerDismissedAt?: Timestamp | null;
}

/**
 * Default onboarding state for new teams
 */
export const defaultTeamOnboarding: Omit<TeamOnboarding, 'createdAt'> = {
  initializedAt: null,
  checklist: {
    vehicle: false,
    customer: false,
    salesInvoice: false,
    purchaseInvoice: false,
    task: false,
    appointment: false,
  },
};

/**
 * Default user onboarding preferences
 */
export const defaultUserOnboardingPrefs: UserOnboardingPrefs = {
  bannerDismissedAt: null,
};
